import * as $ from 'jquery';

import { CustomInputComponent } from '../custom-input';

import './nested-input.component.scss';

const DATA_ATTR_DISABLED_PLACEHOLDER = 'disabled-placeholder';
const ATTR_SHOW_DISABLED_VALUE = 'show-disabled-value';

const DATA_CURRENT_VALUE = 'current-value';
const DATA_PLACEHOLDER = 'placeholder';
const DATA_ORIGINAL_VALUE = 'original-value';
const DATA_TABINDEX = 'tab-index';

export interface NestedInputHTMLElement extends HTMLElement {
  // max and min are strings because they could be string representation of other value types like dates for subclassing
  // element types
  max: string;
  maxLength: number;
  min: string;
  minLength: number;
  placeholder: string;
  value: string;
  disabled: boolean;
  required: boolean;
  validity: ValidityState;
  checkValidity(): boolean;
  reportValidity(): boolean;
}

type AttrProp = keyof Pick<NestedInputComponent, 'max' | 'maxLength' | 'min' | 'minLength'>;

// keys that need to have their property values synced with the value element's attributes
const ATTR_PROPS: AttrProp[] = [
  'max',
  'maxLength',
  'min',
  'minLength',
];

declare global {
  interface HTMLElementTagNameMap {
    'iw-nested-input': NestedInputHTMLElement;
  }

  interface JQuery {
    iwNestedInput(): JQuery<NestedInputHTMLElement>;
  }
}

export type HTMLAnyInputElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

/**
 * A custom component that supports adding extra content inside a .form-control styled input.
 *
 * Optional attributes:
 *   data-disabled-placeholder: If set, hides the input value and uses the specified placeholder when the input is
 *                              disabled.
 *
 * Usage:
 *   <iw-nested-input [data-disabled-placeholder]="-">
 *     <input type="text" name="..." />
 *     <span>Extra content here</span>
 *   </iw-nested-input>
 */
export class NestedInputComponent<TElement extends HTMLElement = NestedInputHTMLElement, TValue = string>
  extends CustomInputComponent<TElement> {

  public static COMPONENT_SELECTOR: string = 'iw-nested-input';

  public static loadPlugin(): void {
    super.loadPlugin();
    if (!$.fn.iwNestedInput) {
      $.fn.iwNestedInput = this.jQueryPlugin('NestedInputComponent');
    }
  }

  #_disabled: boolean;
  public get disabled(): boolean {
    return this.#_disabled;
  }
  public set disabled(disabled) {
    const isChange = (!!this.#_disabled) !== (!!disabled);
    this.#_disabled = !!disabled;
    if (isChange) {
      this.onDisabledChange(this.#_disabled);
    }
  }

  protected _placeholder: string;
  public get placeholder(): string {
    return this._placeholder;
  }
  public set placeholder(placeholder) {
    this._placeholder = placeholder;

    if (this.disabled && this.disabledPlaceholder) {
      return;
    }

    if (typeof placeholder === 'undefined') {
      this.$display.removeProp('placeholder');
    } else {
      this.$display.prop('placeholder', placeholder);
    }
  }

  public get disabledPlaceholder(): string | boolean {
    return this.$el.data(DATA_ATTR_DISABLED_PLACEHOLDER);
  }

  public get value(): string {
    return this.$value.val() as string;
  }
  public set value(value: string) {
    if (value !== this.value) {
      this.$el.attr('value', value); // so the attribute updates in the DOM
      this.setDisplay(value);
      this.setValue(value);
      this.$el.trigger('change');
    }
  }

  public get max(): any {
    return this.getAttrProp('max');
  }
  public set max(value: any) {
    this.setAttrProp('max', value);
  }

  public get maxLength(): number {
    return this.getAttrProp('maxLength');
  }
  public set maxLength(value: number) {
    this.setAttrProp('maxLength', value);
  }

  public get min(): any {
    return this.getAttrProp('min');
  }
  public set min(value: any) {
    this.setAttrProp('min', value);
  }

  public get minLength(): number {
    return this.getAttrProp('minLength');
  }
  public set minLength(value: number) {
    this.setAttrProp('minLength', value);
  }

  public get required(): boolean {
    return this.getAttrProp('required');
  }
  public set required(value: boolean) {
    this.setAttrProp('required', value);
  }

  public get showDisabledValue(): boolean {
    return this.$el.attr(ATTR_SHOW_DISABLED_VALUE) !== undefined;
  }
  public set showDisabledValue(value: boolean) {
    if (value) {
      this.$el.attr(ATTR_SHOW_DISABLED_VALUE);
    } else {
      this.$el.removeAttr(ATTR_SHOW_DISABLED_VALUE);
    }
  }

  /**
   * Returns the value set at the time of initialization, if any
   */
  public get originalValue(): any {
    return this.$el.data(DATA_ORIGINAL_VALUE);
  }

  /**
   * Gets the current value, regardless of disabled state.
   */
  public get currentValue(): any {
    return this.$el.data(DATA_CURRENT_VALUE);
  }

  public get isModified(): boolean {
    return this.originalValue?.valueOf() !== this.currentValue?.valueOf();
  }

  public readonly $value: JQuery<HTMLAnyInputElement>;
  public readonly valueEl: HTMLAnyInputElement;
  public readonly $display: JQuery;
  public readonly $displayPlaceholder: JQuery;

  constructor($el: JQuery<TElement>) {
    super($el);

    this.$display = this.getDisplayInput();
    this.$displayPlaceholder = this.getDisplayPlaceholder();
    this.$value = this.getValueInput();
    this.valueEl = this.$value[0];

    this.required = this.$value.prop('required') || typeof this.$el.attr('required') !== 'undefined';
    this.disabled = this.$value.prop('disabled') || typeof this.$el.attr('disabled') !== 'undefined';
    this.placeholder = this.$display.prop('placeholder') || this.$el.attr('placeholder');
    this.$el.data(DATA_PLACEHOLDER, this.placeholder);

    this.init();

    // define these property setters/getters so that they can be set via jQuery
    Object.defineProperties(this.el, {
      disabled: { // $('iw-nested-input').prop('disabled')
        get: () => this.disabled,
        set: (disabled) => this.disabled = !!disabled,
      },
      showDisabledValue: {
        get: () => this.showDisabledValue,
        set: (showDisabledValue) => this.showDisabledValue = showDisabledValue,
      },
      placeholder: {
        get: () => this.placeholder,
        set: (placeholder) => this.placeholder = placeholder,
      },
      value: { // $('iw-nested-input').val()
        get: () => this.value,
        set: (value) => this.value = value,
      },
      validity: {
        get: () => this.valueEl?.validity,
      },
      checkValidity: {
        value: this.valueEl?.checkValidity.bind(this.valueEl),
      },
      reportValidity: {
        value: this.valueEl?.reportValidity.bind(this.valueEl),
      },
      required: {
        get: () => this.required,
        set: (value) => this.required = value,
      },
      // keys that need to have their property values synced with the value element's attributes
      ...ATTR_PROPS.reduce((result, key) => {
        result[key] = {
          get: () => this[key],
          set: (value: NestedInputComponent[typeof key]) => this[key] = value,
        };
        return result;
      }, {} as any),
    });
  }

  /**
   * Returns a {@link JQuery} object representing the input element(s) that the user will be able see and interact with
   */
  protected getDisplayInput(): JQuery {
    return this.getValueInput();
  }

  /**
   * Returns a {@link JQuery} object representing the input element(s) that the will be used to display the placeholder
   * value. This may be the same as {@link getDisplayInput}.
   */
  protected getDisplayPlaceholder(): JQuery {
    return this.getDisplayInput();
  }

  /**
   * Returns a {@link JQuery} object representing the input element(s) that the will be used to represent the value of
   * the input component. This may be the same as {@link getDisplayInput}.
   */
  protected getValueInput(): JQuery<HTMLAnyInputElement> {
    return this.$el.find(':input') as JQuery<HTMLAnyInputElement>;
  }

  /**
   * Encapsulates all initialization logic that can be customized by subclasses
   */
  protected init(): void {
    // TODO: move all of this except initValue to CustomComponent
    this.initDomRefs();
    this.validateHostAttributes();
    this.initDom();
    this.initValue();
    this.initAttrProps();
    this.initEvents();
  }

  /**
   * Hook for subclasses to find and initialize references to child elements created by the template
   */
  protected initDomRefs(): void {}

  /**
   * Copies the "value" attribute from the host element to the value element, saves the original value in a data
   * property
   */
  protected initValue(): void {
    const value = this.findOriginalValue() || '';
    const importedValue = this.importValue(value);
    this.setValue(this.formatValue(importedValue));
    this.$el
      .data(DATA_ORIGINAL_VALUE, value)
      .data(DATA_CURRENT_VALUE, value);
  }

  /**
   * Hook for subclasses to make any additional updates to the DOM
   */
  protected initDom(): void {}

  /**
   * Syncs attribute values from the host element to corresponding attributes on the value element
   */
  protected initAttrProps(): void {
    ATTR_PROPS.forEach(name => this.setAttrProp(name, this.$el.attr(name)));
  }

  /**
   * Hook for subclasses to validate parsed values from attributes
   */
  protected validateHostAttributes(): void {}

  /**
   * Set up event handlers
   */
  protected initEvents(): void {
    // focus the first input when clicking directly on the element itself or any non-input child element
    this.$el.on('click', (e) => {
      if ($(e.target).is(':not(:input)')) {
        this.$display.first().focus();
      }
    });

    this.$el.on('change', () => {
      this.$el.data(DATA_CURRENT_VALUE, this.value);
    });
  }

  /**
   * Returns the string representation of the original value as defined in the initial rendering of the document
   */
  protected findOriginalValue(): string {
    return this.$el.attr('value') || this.$value.val() as string;
  }

  /**
   * Sets the representation of the value that is shown in the UI
   */
  protected setDisplay(value: string): void {
    this.$display.val(value);
  }

  /**
   * Optionally convert the string representation of a value to a parsed value
   */
  protected importValue(value: string): TValue {
    // TODO: extract more of TemporalComponent's importValue / syncValue / onValue pattern to standardize the value
    //       processing pipeline for "nested" components
    return value as any;
  }

  /**
   * Updates the value element's value property with a new value
   */
  protected setValue(value: string): void {
    this.$value.val(value);
  }

  /**
   * Convert a parsed value to its string representation for storing in the value element's value property
   */
  protected formatValue(value: TValue): string {
    if (typeof value === 'undefined') {
      return '';
    }
    return value.toString();
  }

  protected onDisabledChange(disabled: boolean): void {
    this.$display.prop('disabled', disabled);
    if (disabled) {
      this.$el
        .attr('disabled', '')
        // remove the tabindex attribute so that the element cannot be given focus when disabled
        .data(DATA_TABINDEX, this.$el.attr('tabindex'))
        .removeAttr('tabindex');
    } else {
      this.$el
        .removeAttr('disabled')
        // restore the previously set tabindex
        .attr('tabindex', this.$el.data(DATA_TABINDEX));
    }

    this.updatePlaceholderState(disabled);
  }

  protected syncPlaceholder(disabled: boolean): void {
    const enabledPlaceholder = this.placeholder || '';
    const disabledPlaceholder = this.disabledPlaceholder || enabledPlaceholder;
    this.$displayPlaceholder.prop('placeholder', disabled ? disabledPlaceholder : enabledPlaceholder);
  }

  protected updatePlaceholderState(disabled: boolean): void {
    if (disabled) {
      if (this.disabledPlaceholder && !this.showDisabledValue) {
        this.setDisplay('');
      }
    } else {
      this.setDisplay(this.currentValue);
    }

    const nonInputContentVisibility = disabled && !!this.disabledPlaceholder && !this.showDisabledValue ? 'hidden' : 'visible';
    this.$el.find(':not(:input)').css('visibility', nonInputContentVisibility);

    this.syncPlaceholder(disabled);
  }

  // TODO: add mapped types for supported attribute-based properties
  protected getAttrProp(name: string): any {
    return this.$value.prop(name);
  }

  protected setAttrProp(name: string, value: any): void {
    this.$value.prop(name, value);
  }
}
